import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  EmployerMemberDataService,
  MemberResultsResponse,
  MemberSummaryResponse,
  OrgClientSummary,
  ProgramListItem,
} from './employer-member-data.service';

@Injectable()
export class ApiEmployerMemberDataService implements EmployerMemberDataService {
  constructor(
    private readonly client: RestClientService,
    private readonly transloco: TranslocoService,
  ) {}

  getPrograms(masterOnly = true): Observable<ProgramListItem[]> {
    return this.client
      .get<
        ProgramListItem[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs`), { masterOnly: String(masterOnly) })
      .pipe(
        map(({ body }) => body as ProgramListItem[]),
        catchError(() => throwError(() => new Error(this.transloco.translate('translations.errors.errorGetEmployerPrograms')))),
      );
  }

  getClientPrograms(orgClientId: string): ObservableResult<Page<ProgramItem>> {
    return this.client
      .get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/clients/${orgClientId}`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.transloco.translate('translations.errors.errorGetEmployerPrograms'))),
      );
  }

  getClientSummary(clientId: string): ObservableResult<OrgClientSummary> {
    return this.client
      .get<OrgClientSummary>(Location.joinWithSlash(environment.apiRootUrl || '', `organizations/clients/${clientId}/summary`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.transloco.translate('translations.errors.errorGetClientPermissions'))),
      );
  }

  getMemberSummary(programId: string, memberUid: string): Observable<MemberSummaryResponse> {
    return this.client
      .get<MemberSummaryResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/members/${memberUid}/summary`),
      )
      .pipe(
        map(({ body }) => body as MemberSummaryResponse),
        catchError(() => throwError(() => new Error(this.transloco.translate('translations.errors.errorGetMemberSummary')))),
      );
  }

  getMemberResults(programId: string, memberUid: string): Observable<MemberResultsResponse> {
    return this.client
      .get<MemberResultsResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/members/${memberUid}/results`),
      )
      .pipe(
        map(({ body }) => body as MemberResultsResponse),
        catchError(() => throwError(() => new Error(this.transloco.translate('translations.errors.errorGetMemberResults')))),
      );
  }
}
